// src/pages/homepage-with-3d-construction-experience/components/clients/ClientsBanner.jsx
import React, { useMemo, useRef, useEffect, useState } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import ClientCard from "./ClientCard";
import ClientModal from "./ClientModal";
import { CLIENTS } from "./clientsData";

const ORANGE = "#F27E33";
const NAVY = "#1D2946";
const BASE_BG = "#0B1620";
const DURATION = 92; // Duración en segundos para un ciclo completo (menor = más rápido)
const CLIENTS_OVERLAY = "/assets/images/fondo3.webp";

export default function ClientsBanner() {
  const [selected, setSelected] = useState(null);
  const onOpen = (item) => setSelected(item);
  const onClose = () => setSelected(null);

  // Duplicamos el array de clientes para crear el efecto de bucle visual
  const track = useMemo(() => [...CLIENTS, ...CLIENTS], []);

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const x = useMotionValue(0);
  const animationControls = useRef(null);

  // Mide el ancho del carrusel una vez montado
  useEffect(() => {
    const measureWidth = () => {
      if (containerRef.current) {
        // El ancho de un ciclo es la mitad del ancho total del track
        setContainerWidth(containerRef.current.scrollWidth / 2);
      }
    };

    // Pequeña demora para asegurar que el contenido se ha renderizado
    const timeoutId = setTimeout(measureWidth, 100);
    window.addEventListener("resize", measureWidth);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", measureWidth);
    };
  }, [track]);

  // Función mejorada para iniciar y reanudar la animación de bucle
  const startAnimation = (startX = 0) => {
    animationControls.current?.stop(); // Detiene cualquier animación previa

    // Calcula la duración restante para mantener una velocidad constante
    const remainingDistance = -containerWidth - startX;
    const duration = (remainingDistance / -containerWidth) * DURATION;

    animationControls.current = animate(x, [startX, -containerWidth], {
      duration: duration,
      ease: "linear",
      onComplete: () => {
        x.set(0); // Vuelve al inicio sin que el usuario lo note
        startAnimation(0); // Reinicia el bucle
      },
    });
  };

  // Inicia la animación cuando el componente está listo
  useEffect(() => {
    if (containerWidth > 0) {
      startAnimation();
    }
    return () => animationControls.current?.stop();
  }, [containerWidth]);

  // Nueva lógica para los botones de navegación
  const handleMove = (direction) => {
    animationControls.current?.stop();

    const stepPx = containerWidth / 4; // Mueve un 25% del carrusel por clic
    const currentX = x.get();
    let targetX = currentX + direction * stepPx;

    // Anima el desplazamiento del clic con un efecto suave (spring)
    animate(x, targetX, {
      type: "spring",
      stiffness: 400,
      damping: 60,
      onComplete: () => {
        let finalX = x.get();
        // Lógica para "envolver" la posición si se sale de los límites
        if (finalX > 0) {
          finalX %= -containerWidth;
          x.set(finalX);
        } else if (finalX < -containerWidth) {
          finalX %= -containerWidth;
          x.set(finalX);
        }
        // Reanuda la animación de bucle desde la nueva posición
        startAnimation(finalX);
      },
    });
  };

  const moveLeft = () => handleMove(1);  // Mueve hacia la izquierda (valor positivo)
  const moveRight = () => handleMove(-1); // Mueve hacia la derecha (valor negativo)

  return (
    <section
      className="relative py-16 lg:py-20 overflow-hidden"
      style={{ background: BASE_BG }}
    >
      {/* Fondo y Header (sin cambios) */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${CLIENTS_OVERLAY}?v=${Date.now()})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(14px) saturate(110%)",
            opacity: 0.5,
            transform: "scale(1.06)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(11,22,32,0.15), rgba(11,22,32,0.35) 45%, rgba(11,22,32,0.6) 75%)",
          }}
        />
        <div
          className="absolute -top-10 left-0 right-0 h-10"
          style={{
            background: "linear-gradient(to bottom, rgba(0,0,0,0), rgba(11,22,32,1))",
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <div
          className="mx-auto mb-6 h-[3px] w-24 rounded-full"
          style={{ background: `linear-gradient(90deg, ${ORANGE}, ${NAVY})` }}
        />
        <h2 className="font-orbitron font-extrabold text-3xl sm:text-4xl text-white tracking-tight">
          Aliados que confían en nosotros
        </h2>
        <p className="font-inter text-white/85 mt-3 max-w-3xl mx-auto">
          Casos destacados de mantenimiento, adecuaciones y obra en salud, banca, educación e industria.
        </p>
      </div>

      {/* Carrusel */}
      <div
        className="relative z-10 mt-10 select-none"
        role="region"
        aria-label="Clientes y casos"
      >
        <div className="relative">
          <div className="relative overflow-hidden px-4 sm:px-0">
            <motion.div
              ref={containerRef}
              className="flex items-stretch gap-4 sm:gap-6 will-change-transform min-h-[390px] sm:min-h-[420px]"
              style={{ x, width: "max-content" }}
            >
              {track.map((item, idx) => (
                <ClientCard key={`${item.id}-${idx}`} item={item} onOpen={onOpen} />
              ))}
            </motion.div>
          </div>

          <div
            className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-24"
            style={{ background: `linear-gradient(90deg, ${BASE_BG}, transparent)` }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-24"
            style={{ background: `linear-gradient(270deg, ${BASE_BG}, transparent)` }}
          />

          {/* Botones de control actualizados */}
          <button
            type="button"
            onClick={moveLeft}
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-xl border border-white/15 bg-white/10 backdrop-blur-sm text-white px-3 py-2 text-2xl leading-none hover:bg-white/15 transition"
            aria-label="Anterior"
            title="Anterior"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={moveRight}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl border border-white/15 bg-white/10 backdrop-blur-sm text-white px-3 py-2 text-2xl leading-none hover:bg-white/15 transition"
            aria-label="Siguiente"
            title="Siguiente"
          >
            ›
          </button>
        </div>
      </div>

      <ClientModal item={selected} onClose={onClose} />
    </section>
  );
}